import React, { useEffect, useRef } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import EstradaNeon from '../EstradaNeon'
import SilhuetaBlumenau from '../SilhuetaBlumenau'
import { comDestaque } from '../../lib/destaque'
import IconeArte from '../../lib/iconeArte'
import { TEMAS_PALCO, ANCORA_INSCRICAO } from '../../dados/evento'

/* Guard de SSR — o Gatsby renderiza este componente em Node no build, e
   `registerPlugin` toca em `document`. Idempotente; outras seções também
   registram. */
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger)
}


/* Cada tema aponta para um disco do KIT pela chave `icone` que já existia no
   dado. Quem não estiver no mapa cai no pino da rota, que é neutro e sempre
   faz sentido nesta página. */
const DISCO_PALCO = {
  'chart-line': 'ico-barras',
  sparkles: 'ico-chip',
  gavel: 'ico-cifrao',
  speakerphone: 'ico-foguete',
  settings: 'ico-engrenagem',
  'users-group': 'ico-aperto',
}

function discoPara(nome) {
  return DISCO_PALCO[nome] || 'ico-pino-logo'
}

/**
 * Entrada do bento — tiles em fade+subida escalonados, uma vez só, quando a
 * grade entra na tela. Mesmo padrão de entrada `once` das outras dobras
 * (ScrollTrigger + clearProps no cleanup). Sem JS (ou com
 * `prefers-reduced-motion: reduce`) nada roda e a grade fica simplesmente
 * visível — os `gsap.set` que escondem só executam dentro do efeito.
 */
function useEntradaPalco(quantidade) {
  const gradeRef = useRef(null)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const grade = gradeRef.current
    if (!grade || quantidade < 1) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const tiles = Array.from(grade.querySelectorAll('.palco-tile'))
    if (tiles.length === 0) return

    gsap.set(tiles, { opacity: 0, y: 24 })

    const tl = gsap.timeline({
      scrollTrigger: { trigger: grade, start: 'top 78%', once: true },
    })
    tiles.forEach((tile, i) => {
      tl.to(tile, { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' }, i * 0.09)
    })

    return () => {
      tl.scrollTrigger?.kill()
      tl.kill()
      gsap.set(tiles, { clearProps: 'all' })
    }
  }, [quantidade])

  return gradeRef
}

/**
 * 2ª dobra — o que vai estar no palco, em BENTO GRID assimétrico: mosaico de
 * tiles em tamanhos mistos (1º e último temas em tiles largos de duas
 * colunas, nos cantos opostos da grade; os quatro do meio em tiles
 * quadrados). Decisão de 11/08 (8ª iteração): a versão anterior — mapa de
 * rota com marcos numerados e linha tracejada — usava exatamente o mesmo
 * vocabulário de "Pra quem é" (dobra 4), e as duas seções liam como
 * repetição. O bento diferencia na raiz: nenhum marco, nenhum tracejado —
 * aqui o protagonista é o ÍCONE de cada tema, com a numeração reduzida a um
 * carimbo discreto no canto. Referência do padrão: Apple (WWDC), Linear,
 * Stripe — o formato dominante de grade de features desde 2023.
 *
 * A identidade da dobra continua no fundo (cena de asfalto com grão), no
 * hover (mesma elevação dos outros cartões) e na `EstradaNeon` que preenche
 * o tile de destaque. Tudo visível de uma vez, sem nenhuma rolagem interna —
 * a exigência que derrubou o scroll-jacking e o carrossel continua valendo.
 *
 * Os spans por posição (1º e último largos) assumem os 6 temas do briefing;
 * com outra quantidade a grade degrada para o fluxo automático — aceitável
 * para um cenário que o Builder não tem hoje.
 */
export default function SecaoPalco({
  chamada = 'Conteúdo, tecnologia e eficiência para elevar o nível da gestão imobiliária.',
  chamadaDestaque = 'elevar o nível da gestão imobiliária.',
  subtitulo = 'O que vai estar no palco do Rota da Eficiência',
  temas = TEMAS_PALCO,
  textoCta = 'Garantir minha presença',
}) {
  const gradeRef = useEntradaPalco(temas.length)

  return (
    /* CHÃO CLARO — pedido do cliente no PDF de 12/08 ("neste bloco colocar o
       nauta e transformar ele em um bloco de fundo branco para quebrar um
       pouco"). A dobra troca `.noite` por `.dia`: os tokens de cor viram os do
       mundo claro de uma vez só, e `.dobra-clara` (revisao.css) sobrescreve o
       `background-color: var(--noite-1)` que arte.css crava aqui.

       É a primeira das quatro pausas claras da página (palco, Apre.Lab,
       inscrição, sobre). Ver o cabeçalho de revisao.css para o ritmo inteiro. */
    <section className="secao secao-rota dia dobra-clara" id="palco">
      <div className="rota-palco-cena">
        {/* Cena de fundo da lâmina: pino e rota entrando pela esquerda, casario
            de Blumenau na base. Decorativos.

            O NAUTA SAIU DAQUI em 13/08 e virou filho do cabeçalho (ver abaixo).
            Como irmão da cena ele era `position: absolute` na quina superior
            direita, fora de qualquer coluna — e a 1920px isso o deixava
            flutuando acima do vão que o próprio cabeçalho abre à direita, em
            vez de dentro dele. */}
        <EstradaNeon className="palco-rota" />
        <SilhuetaBlumenau className="silhueta palco-silhueta" />

        <div className="container-site">
          {/* Cabeçalho À ESQUERDA (12/08). Ele foi centralizado em 11/08 para
              casar com as lâminas, junto com o de todas as outras dobras — e
              foi assim que a página passou a ter uma família de layout só.
              Aqui ele volta ao eixo esquerdo porque a dobra seguinte
              (palestrantes) abre centrada: o contraste entre as duas é o que
              cria ritmo.

              A placa também saiu. Eram seis `.eyebrow` em onze dobras, e o
              título já diz "no palco" na primeira palavra. */}
          {/* DUAS COLUNAS a partir de 1201px (13/08, pedido do cliente): o
              título ocupa ~3/4 e o Nauta mora no quarto restante. O cabeçalho
              vinha travado em 46rem por `.v2-cabecalho-esq`, e a 1920px isso
              deixava 864px de vão morto à direita — o buraco que o cliente
              marcou na captura.

              O NAUTA É FILHO DO CABEÇALHO, não da cena: assim ele ocupa uma
              coluna de verdade e acompanha o título em qualquer largura, em vez
              de depender de percentuais sobre a altura da seção (que muda com o
              número de linhas do título). Continua decorativo — `alt=""` e sem
              alcance de ponteiro. Abaixo de 1201px o arquivo é escondido por
              arte.css e o cabeçalho volta a ser uma coluna só. */}
          <div className="palco-cabecalho cabecalho-secao v2-cabecalho-esq">
            <h2>{comDestaque(chamada, chamadaDestaque)}</h2>
            <p>{subtitulo}</p>
            {/* 840×638 é o arquivo REAL (crop do alfa do original, ver
                nauta-rota-1-web-2x.png). O `-web` de 560px ficava mole no
                tamanho novo, e os antigos 420×420 declarados aqui mentiam a
                proporção — o navegador reservava uma caixa quadrada para uma
                arte 1,32:1. */}
            <img
              className="palco-nauta"
              src="/img/nauta-rota-1-web-2x.png"
              alt=""
              width="840"
              height="638"
              loading="lazy"
            />
          </div>

          {/* BENTO ASSIMÉTRICO, de volta em 12/08.
              A fileira de seis cartões idênticos era o tell de layout mais
              reconhecível da página: seis colunas do mesmo peso, com a mesma
              copy de cinco linhas espremida em cada uma, a 1440px.

              Agora o 1º e o 6º tema ocupam tiles de duas colunas em quinas
              opostas (são os dois que trazem foto no dado) e os quatro do meio
              ficam compactos. O CSS faz o span por posição; a marcação segue
              uma lista só. Ver v2.css §4. */}
          <ol className="palco-grade lista-limpa" ref={gradeRef}>
            {temas.map((tema) => {
              return (
                <li key={tema.titulo} className="palco-tile cartao cartao-arte">
                  <span className="palco-tile-selo" aria-hidden="true">
                    <IconeArte nome={discoPara(tema.icone)} />
                  </span>
                  <h3>{tema.titulo}</h3>
                  <span className="cartao-fio" aria-hidden="true" />
                  <p>{tema.texto}</p>
                </li>
              )
            })}
          </ol>

          {/* CTA REBAIXADO PARA LINK (12/08). A página tinha seis botões
              cheios, todos apontando para #inscricao com rótulos diferentes
              ("Quero estar presente", "Garantir ingresso", "Quero viver essa
              experiência"...). Seis chamadas de mesma intenção competindo
              entre si não somam: dividem.

              Ficaram três botões cheios (cabeçalho, hero, Apre.Lab) e o mesmo
              rótulo em todos, porque a ação é a mesma. Aqui e em "para quem
              é" a intenção vira link com seta. */}
          {/* CTA CHEIO (12/08, PDF do cliente). A referência que ele anexou
              para esta dobra fecha com um botão azul sólido embaixo da grade,
              não com um link de texto. O RÓTULO continua sendo o mesmo da
              página inteira: a referência traz "Quero estar presente", e ter
              dois rótulos para a mesma ação foi justamente o defeito corrigido
              em 12/08 (seis CTAs, seis frases diferentes). */}
          <div className="secao-acao">
            {/* ⚠️ A SETA É `<span>`, NÃO O `<svg>` QUE ESTAVA AQUI. O SVG do
                link de texto não traz `width`/`height` no elemento e só ganha
                tamanho pela regra `.v2-cta-texto svg` — dentro de um `.btn`
                ele caía no tamanho padrão de elemento substituído (300×150) e
                o botão saía com 314×159, com o rótulo quebrado em três
                linhas. Medido na captura de QA de 12/08. */}
            <a href={ANCORA_INSCRICAO} className="btn btn-rota">
              {textoCta}
              <span className="btn-seta" aria-hidden="true">→</span>
            </a>
          </div>

          {/* A assinatura da Rota fecha a dobra, como em toda lâmina do kit. */}
      {/* A FAIXA "Menos atrito · Mais clareza · Mais eficiência · Melhores
          resultados" SAIU em 12/08, a pedido do cliente.

          Ela aparecia TRÊS vezes na mesma página — no fim do palco, no fim dos
          palestrantes e no rodapé — sempre com as mesmas quatro frases e os
          mesmos quatro ícones. Numa peça impressa a assinatura repetida
          funciona porque cada lâmina é vista isolada; numa página que rola, a
          terceira aparição não assina nada, só avisa que o conteúdo acabou de
          novo.

          O componente `AssinaturaRota` e o dado `ASSINATURA_ROTA` continuam
          existindo, sem consumidor: são conteúdo editável pelo Builder e
          removê-los quebraria qualquer entrada que já os referencie. */}
        </div>
      </div>
    </section>
  )
}
